/**
 * Asking the browser not to throw away the offline queue.
 *
 * ## Why
 *
 * Everything this app keeps offline lives in IndexedDB: the active-workout
 * write queue today, and a cached plan plus pending sessions if the offline
 * logger is ever built. By default that storage is BEST-EFFORT - the browser
 * may evict it under storage pressure, and iOS additionally clears
 * script-writable storage for sites it considers unused after roughly seven
 * days. A home-screen PWA opened three times a week is not obviously "used".
 *
 * `navigator.storage.persist()` moves the origin to the "persistent" bucket,
 * which is not evicted automatically. It was never called anywhere in this
 * codebase, so the durability the write queue advertises has been resting on a
 * default that does not promise it.
 *
 * ## Why the answer has to be shown, not just requested
 *
 * The request can be REFUSED, silently, and refusal is not rare - Safari grants
 * it on its own criteria and does not explain them. A feature whose whole
 * promise is "your sets are safe on this device" cannot treat that as a
 * detail, so Settings reports what the browser actually said rather than what
 * we asked for.
 *
 * Nothing here throws. Every call is wrapped: the Storage API is missing in
 * older WebViews, and blocked outright in some private modes.
 */

export type StorageStatus = {
  /** Does this browser expose the Storage API at all? */
  supported: boolean;
  /** Is storage in the persistent bucket right now? Null when unknown. */
  persisted: boolean | null;
  /** Bytes currently used by this origin, when the browser will say. */
  usageBytes: number | null;
  /** Bytes the browser is willing to give this origin, when it will say. */
  quotaBytes: number | null;
};

/**
 * Ask for persistence if we do not already have it.
 *
 * Safe to call on every load: `persisted()` is checked first, so a granted
 * origin never re-prompts. Returns the resulting state rather than a bare
 * boolean so a caller can tell "refused" from "cannot ask".
 */
export async function requestPersistentStorage(): Promise<boolean | null> {
  if (typeof navigator === "undefined" || !navigator.storage) return null;
  try {
    if (typeof navigator.storage.persisted === "function") {
      const already = await navigator.storage.persisted();
      if (already) return true;
    }
    if (typeof navigator.storage.persist !== "function") return null;
    return await navigator.storage.persist();
  } catch {
    // Blocked storage / private mode. Not knowing is different from being
    // refused, and the caller is allowed to tell them apart.
    return null;
  }
}

/** Read the current state without asking for anything. */
export async function readStorageStatus(): Promise<StorageStatus> {
  const none: StorageStatus = {
    supported: false,
    persisted: null,
    usageBytes: null,
    quotaBytes: null,
  };
  if (typeof navigator === "undefined" || !navigator.storage) return none;

  let persisted: boolean | null = null;
  try {
    if (typeof navigator.storage.persisted === "function") {
      persisted = await navigator.storage.persisted();
    }
  } catch {
    persisted = null;
  }

  let usageBytes: number | null = null;
  let quotaBytes: number | null = null;
  try {
    if (typeof navigator.storage.estimate === "function") {
      const est = await navigator.storage.estimate();
      usageBytes = typeof est.usage === "number" ? est.usage : null;
      quotaBytes = typeof est.quota === "number" ? est.quota : null;
    }
  } catch {
    /* estimate() is optional and throws in some private modes. */
  }

  return { supported: true, persisted, usageBytes, quotaBytes };
}

/** Bytes as something a human reads at a glance. Deliberately coarse. */
export function formatBytes(bytes: number | null): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return "unknown";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}
